import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { faBox, faBoxesStacked } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LineBatchMeta } from '@shared/components/ui/LineBatchMeta';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { hasRenderableLineBatchMeta } from '@shared/utils/sanitizeBatchDisplay';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StockLocationCart } from '../../models/Cart';

export interface CartProductsModalProps {
  cartCode: string;
  products: StockLocationCart[];
}

export interface CartProductsModalRef {
  open: () => void;
  close: () => void;
}

export const CartProductsModal = forwardRef<CartProductsModalRef, CartProductsModalProps>((props, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const { t } = useTranslation('cartsModule');

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  // Calcular total de unidades
  const totalUnits = props.products.reduce((sum, p) => sum + (p.qty || 0), 0);

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBoxesStacked} className="w-4 h-4" />
          {t('products_modal.title')} - {props.cartCode}
        </span>
      }
      type="default"
      width="lg"
      className="w-full h-full sm:w-lg sm:h-auto sm:max-w-[579px]"
    >
      {/* Alerta informativa */}
      <Alert className="border-primary dark:border-primary/30 mb-6">
        <div className="flex items-center gap-3">
          <div>
            <div className="w-8 h-8 bg-primary/10 dark:bg-primary/20 flex items-center justify-center rounded-full">
              <FontAwesomeIcon icon={faBox} className="text-primary w-3.5 h-3.5" />
            </div>
          </div>
          <div>
            <div className="font-semibold text-foreground">{t('products_modal.products_in_cart')}</div>
            <AlertDescription className="text-sm text-muted-foreground">
              {t('products_modal.total_units', { count: totalUnits })}
            </AlertDescription>
          </div>
        </div>
      </Alert>

      {/* Lista de productos */}
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {props.products.length > 0 ? (
          <>
            {props.products.map((stockItem, i) => (
              <div
                key={`${stockItem.product_id}-${stockItem.location_id}-${stockItem.batch_id ?? 'nb'}-${i}`}
                className="flex items-center justify-between gap-2 border-b border-card-border pb-4"
              >
                <div className="min-w-0 flex-1">
                  <Product
                    id={stockItem.product.id}
                    src={stockItem.product.image || ''}
                    name={stockItem.product.name}
                    code={stockItem.product.code || ''}
                    acronym={stockItem.product.name?.[0] || '?'}
                    className="min-w-0"
                    enableLineClamp={false}
                    showImageModal={true}
                  />
                  {stockItem.batch &&
                  hasRenderableLineBatchMeta({
                    batch: stockItem.batch,
                    batchId: stockItem.batch_id ?? stockItem.batch?.id ?? null,
                    hideNumericIdFallback: false,
                  }) ? (
                    <div className="mt-2 pl-10 sm:pl-11">
                      <LineBatchMeta
                        batch={stockItem.batch}
                        batchId={stockItem.batch_id ?? stockItem.batch?.id ?? undefined}
                        i18nNs="cartsModule"
                        lotLabelKey="products_modal.batch_label"
                        expiryLabelKey="products_modal.expiry_label"
                      />
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-col items-end shrink-0">
                  <div className="text-sm text-muted-foreground">{t('products_modal.quantity')}</div>
                  <div className="font-semibold text-lg text-foreground">{stockItem.qty}</div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <div className="text-center py-8">
            <FontAwesomeIcon icon={faBox} className="w-12 h-12 text-muted-foreground/30 mb-2" />
            <p className="text-muted-foreground">{t('products_modal.no_products')}</p>
          </div>
        )}
      </div>
    </ModalCenter>
  );
});

CartProductsModal.displayName = 'CartProductsModal';
